<template>
    <div ref="containerGradientRef" class="c-gradient-picker">
        <div class="c-gradient-picker__header">
            <c-dropdown
                class="w-50"
                :options="getGradientTypes"
                :value="gradientType"
                :name="'gradientType'"
                :disabled="true"
                placeholder="Select Gradient Type"
                @input="onGradientTypeChange"
            />
            <c-input
                v-model="gradientAngle"
                :min="0"
                :max="180"
                label=""
                type="number"
                input-size="sm"
                class="c-gradient-picker__angle-input"
                @input="onUpdateAngle"
            >
                <template #append>
                    <span>°</span>
                </template>
            </c-input>
        </div>
        <div ref="containerRef" class="c-gradient-picker__canvas" @dblclick="addColorStop">
            <div class="c-gradient-picker__bar">
                <div class="c-gradient-picker__bar-fill" :style="gradientStyle" />
            </div>
            <div
                v-for="(stop, index) in gradientColorStopList"
                :key="`picker-${index}`"
                class="c-gradient-picker__stop"
                :style="{ left: getGradientStopPosition(stop.offset) }"
                :class="{ active: selectedStopIndex === index }"
                @mousedown.stop="startDragging(index, $event)"
            >
                <div class="c-gradient-picker__stop-handle" :style="innerStopBackground(stop.hex)" />
            </div>
        </div>
        <div class="c-gradient-picker__stops">
            <div class="c-gradient-picker__stops-header">
                <h3>Stops</h3>
                <c-button size="icon" icon="plus" @click="() => addColorStop()" />
            </div>
            <div class="c-gradient-picker__stops-list">
                <div
                    v-for="stop in sortedGradientStops"
                    :key="`list-${stop.index}`"
                    :data-index="stop.index"
                    class="c-gradient-picker__stops-item"
                    :class="{ active: selectedStopIndex === stop.index }"
                >
                    <c-input
                        :value="stop.offset"
                        input-size="sm"
                        :min="0"
                        :max="100"
                        type="number"
                        @input="(e) => onOffsetChange(stop, e)"
                    />
                    <div class="c-gradient-picker__stops-color">
                        <div
                            class="c-gradient-picker__stops-preview"
                            :style="{ backgroundColor: stop.hex }"
                            @click.stop="() => openColorPickerPopper(stop.index)"
                        />
                        <span>{{ stop.hex.replace('#', '').toUpperCase() }}</span>
                    </div>
                    <c-button
                        :disabled="!canDeleteStop"
                        size="icon"
                        icon="trash-can"
                        @click="() => onDeleteStop(stop.index)"
                    />
                </div>
            </div>
        </div>
        <div ref="solidColorRef" class="color-selector-wrapper">
            <c-solid-color
                v-if="openColorPicker"
                v-model="currentColor"
                class="color-picker"
                :mode="mode"
                @input="onUpdateColor"
            >
                <template #footer>
                    <div class="d-flex justify-content-end mt-auto">
                        <c-button
                            class="mr-2"
                            variant="outline-primary"
                            size="sm"
                            text="CANCEL"
                            @click="onClosePopper"
                        />
                        <c-button variant="primary" size="sm" text="APPLY" @click="onClosePopper" />
                    </div>
                </template>
            </c-solid-color>
        </div>
    </div>
</template>

<script setup lang="ts">
import { Ref, ref, computed, onUnmounted, onMounted, onBeforeMount } from 'vue';
import CDropdown from '@/components/CDropdown.vue';
import CInput from '../CInput';
import CButton from '../CButton/CButton.vue';
import CSolidColor from './CSolidColor.vue';
import Popper from 'popper.js';
import { type FullColorObject, type FullGradientObject, type GradientStopObject, GradientType, useColor } from '@/composables/color';
import { debounce } from 'lodash';

interface GradientStopSorted extends GradientStopObject {
    index: number;
}

const props = withDefaults(
    defineProps<{
        value?: FullGradientObject;
        mode?: 'cmyk' | 'rgb';
    }>(),
    {
        mode: 'rgb'
    }
);

const emit = defineEmits<{
    (event: 'input', value: FullGradientObject): void;
}>();

const getGradientTypes = [
    {
        value: GradientType.LINEAR,
        text: 'Linear'
    },
    {
        value: GradientType.RADIAL,
        text: 'Radial'
    }
];

const gradientColorStopList = ref<GradientStopObject[]>([]);
const gradientAngle = ref(0);
const gradientType = ref<string>(GradientType.LINEAR);
const dragStopIndex = ref<number | null>(null);
const selectedStopIndex = ref<number | null>(null);
const containerRef: Ref<HTMLDivElement | null> = ref(null);
const containerGradientRef: Ref<HTMLDivElement | null> = ref(null);
const solidColorRef: Ref<HTMLDivElement | null> = ref(null);
const currentColor = ref<FullColorObject | undefined>();
const instancePopper = ref<Popper | null>(null);
const openColorPicker = ref(false);
const { getColorObj, getGradientCSS} = useColor();

const sortedGradientStops = computed<GradientStopSorted[]>(() => {
    return gradientColorStopList.value
        .map((stop, index) => ({
            ...stop,
            index
        }))
        .sort((a, b) => a.offset - b.offset);
});

const gradientStyle = computed(() => {
    return getGradientCSS(sortedGradientStops.value, gradientAngle.value);
});

const canDeleteStop = computed(() => {
    return gradientColorStopList.value.length > 2;
});

const getGradientStopPosition = (offset: number) => {
    const wStop = 14;
    return `calc(${offset}% - ${wStop / 2}px)`;
};

const innerStopBackground = (color: string) => {
    return `background-color: ${color};`;
};

const addColorStop = (event?: MouseEvent | TouchEvent) => {
    const container = containerRef.value;
    if (!container) return;

    let offset = 90;
    if (event) {
        const rect = container.getBoundingClientRect();
        const clientX = event instanceof MouseEvent ? event.clientX : (event as TouchEvent).touches[0].clientX;

        offset = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1) * 100;
    } else if (sortedGradientStops.value.length >= 2) {
        const lastStop = sortedGradientStops.value[sortedGradientStops.value.length - 1];
        const secondLastStop = sortedGradientStops.value[sortedGradientStops.value.length - 2];
        offset = (lastStop.offset + secondLastStop.offset) / 2;
        offset = Math.min(Math.max(offset, 0), 100);
    }

    gradientColorStopList.value.push({ hex: '#ffffff', cmyk: { c: 0, m: 0, y: 0, k: 0}, alpha: 1, offset: Math.round(offset) });
    onInputChange();
};

const startDragging = (index: number, event: MouseEvent) => {
    event.preventDefault();
    dragStopIndex.value = index;
    setActiveStop(index);

    document.addEventListener('mousemove', handleDragging);
    document.addEventListener('mouseup', stopDragging);
};

const handleDragging = (event: MouseEvent | TouchEvent) => {
    if (dragStopIndex.value === null || !containerRef.value) return;

    const container = containerRef.value;
    const rect = container.getBoundingClientRect();
    const clientX = event instanceof MouseEvent ? event.clientX : (event as TouchEvent).touches[0].clientX;

    const offset = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1) * 100;

    gradientColorStopList.value[dragStopIndex.value].offset = Math.round(offset);
    onInputChange();
};

const onKeyUp = (event: KeyboardEvent) => {
    if (event.key !== 'Delete' || selectedStopIndex.value === null) return;
    const stop = gradientColorStopList.value[selectedStopIndex.value];
    if (stop) {
        gradientColorStopList.value.splice(selectedStopIndex.value, 1);
        dragStopIndex.value = null;
        selectedStopIndex.value = null;
        onInputChange();
    }
};

const onDeleteStop = (index: number) => {
    if (index < 0 || index >= gradientColorStopList.value.length) return;
    if (gradientColorStopList.value.length <= 2) {
        console.warn('Cannot delete stop, at least two stops are required.');
        return;
    }
    gradientColorStopList.value.splice(index, 1);
    dragStopIndex.value = null;
    selectedStopIndex.value = null;
    onInputChange();
};

const onOffsetChange = (stop: GradientStopSorted, value: string | number) => {
    const oldStop = gradientColorStopList.value[stop.index];
    if (oldStop) {
        oldStop.offset = Math.round(Math.min(Math.max(Number(value), 0), 100));
    }
    onInputChange();
};

const onUpdateAngle = (value: string | number) => {
    const angle = Math.min(Math.max(Number(value), 0), 180);
    gradientAngle.value = angle;
    onInputChange();
};

const onGradientTypeChange = (value: GradientType) => {
    gradientType.value = value;
    onInputChange();
};

const unBindEvents = () => {
    document.removeEventListener('mousemove', handleDragging);
    document.removeEventListener('mouseup', stopDragging);
};

const stopDragging = () => {
    dragStopIndex.value = null;
    unBindEvents();
    onInputChange();
    onClosePopper();
};

const setActiveStop = (index: number) => {
    selectedStopIndex.value = index;
    const colorSelected = gradientColorStopList.value[index]
        ? gradientColorStopList.value[index]
        : undefined;

    currentColor.value = getColorObj({ hex: colorSelected?.hex, a: colorSelected?.alpha || 1 });
};

const onUpdateColor = (color: FullColorObject) => {
    if (selectedStopIndex.value !== null && gradientColorStopList.value[selectedStopIndex.value]) {
        gradientColorStopList.value[selectedStopIndex.value].hex = color.hex;
    }
    onInputChange();
};

const openColorPickerPopper = (index: number) => {
    if (instancePopper.value) {
        instancePopper.value.destroy();
        instancePopper.value = null;
    }
    setActiveStop(index);
    if (!containerGradientRef.value) {
        console.warn('Container reference is not set.');
        return;
    }
    let container: HTMLDivElement = containerGradientRef.value;
    if (containerGradientRef.value?.parentElement) {
        container = containerGradientRef.value.parentElement as HTMLDivElement;
    }
    instancePopper.value = new Popper(container, solidColorRef.value as HTMLDivElement, {
        placement: 'top-end',
        modifiers: {
            flip: {
                enabled: false,
                behavior: ['top', 'bottom'],
                padding: {
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0
                }
            },
            preventOverflow: {
                enabled: true,
                boundariesElement: 'viewport', // prevent overflow on window
                padding: {
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0 // prevent overflow on right side
                }
            },
            hide: {
                enabled: false
            },
            offset: {
                offset: '100px, -25%'
            }
        }
    });
    openColorPicker.value = true;
};

const onClosePopper = () => {
    if (instancePopper.value) {
        instancePopper.value.destroy();
        instancePopper.value = null;
    }
    openColorPicker.value = false;
};

const debounceUpdate = debounce(() => {
    const gradient: FullGradientObject = {
        colorStops: gradientColorStopList.value,
        angle: gradientAngle.value,
        type: gradientType.value as GradientType
    };
    emit('input', gradient);
}, 100);

const onInputChange = () => {
    debounceUpdate();
};

onMounted(() => {
    document.addEventListener('keyup', onKeyUp);
    if (props.value) {
        gradientColorStopList.value = props.value.colorStops || [];
        gradientAngle.value = props.value.angle || 0;
        gradientType.value = props.value.type || GradientType.LINEAR;
    } else {
        gradientColorStopList.value = [
            { hex: '#ffffff', cmyk: { c: 0, m: 0, y: 0, k: 0}, alpha: 1, offset: 0 },
            { hex: '#000000', cmyk: { c: 0, m: 0, y: 0, k: 100}, alpha: 1, offset: 100 }
        ];
        gradientAngle.value = 0;
        gradientType.value = GradientType.LINEAR;
    }
    onInputChange();
});

onBeforeMount(() => {
    onInputChange();
});

onUnmounted(() => {
    unBindEvents();
    document.removeEventListener('keyup', onKeyUp);
});
</script>

<style lang="scss" scoped>
@import 'styles/utilities/functions';

.color-selector-wrapper {
    position: relative;
    width: 28rem;
    z-index: 9999;
    .color-picker {
        position: absolute;
        width: 100%;
        background-color: get-color('white');
        padding: 0.625rem;
        border-radius: 0.3125rem;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15), 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    }
}

.c-gradient-picker {
    width: 100%;
    margin-bottom: 0.625rem;
    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 0.625rem;
    }
    &__angle-input {
        width: 50px;
        :deep(.input-group) {
            flex-wrap: nowrap;
            input[type='number'] {
                width: 100%;
                appearance: textfield;
                -moz-appearance: textfield;
                padding: 0.375rem;
                &::-webkit-outer-spin-button,
                &::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                }
                .border span {
                    font-size: 0.875rem;
                }
            }
        }
    }
    &__canvas {
        display: flex;
        position: relative;
        margin-bottom: 0.625rem;
    }
    &__bar {
        width: 100%;
        position: relative;
        box-sizing: border-box;
        padding-top: 1.25rem;
        &-fill {
            width: 100%;
            height: 0.75rem;
            border-radius: 0.625rem;
            user-select: none;
            outline: none;
        }
    }
    &__stop {
        position: absolute;
        top: 0;
        left: 0;
        width: 0.875rem;
        height: 0.875rem;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid get-color('gray-light');
        background-color: transparent;
        padding: 1px;
        cursor: grab;
        &.active {
            border: 1px solid get-color('purple');
            &::before {
                border-top: 0.25rem solid get-color('purple');
            }
        }
        &::before {
            content: '\A';
            position: absolute;
            left: calc(50% - 0.1875rem);
            bottom: -0.3125rem;
            width: 0;
            height: 0;
            border-left: 0.1875rem solid transparent;
            border-right: 0.1875rem solid transparent;
            border-top: 0.25rem solid get-color('gray-light');
        }
        &-handle {
            display: flex;
            width: 100%;
            height: 100%;
            user-select: none;
        }
    }
    &__stops {
        &-header {
            margin-bottom: 0.625rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
            h3 {
                margin: 0;
                font-size: 0.875rem;
                font-weight: bold;
                color: get-color('gray-600');
            }
        }
        &-list {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            overflow: hidden;
            overflow-y: scroll;
            max-height: 200px;
        }
        &-color {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            width: 100%;
            padding: 0.3125rem;
            border: 1px solid get-color('gray-light');
            border-radius: 0.125rem;
            background-color: get-color('white');
        }
        &-preview {
            width: 1.25rem;
            height: 1.0625rem;
            border-radius: 0.125rem;
            border: 1px solid get-color('gray-light');
            cursor: pointer;
        }
        &-item {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.5rem;
            align-items: center;
            justify-content: space-between;
            &.active {
                background-color: get-color('purple-lightest');
            }
        }
    }
}
</style>
